import { useState, useRef } from 'react';
import { Upload, X, Image as ImageIcon, Camera, Plus } from 'lucide-react';

interface PhotoUploadProps {
  onUpload: (photos: File[]) => void;
  maxPhotos?: number;
  maxSizePerPhoto?: number; // in MB
  allowMultiple?: boolean;
  className?: string;
}

const PhotoUpload = ({ 
  onUpload, 
  maxPhotos = 5, 
  maxSizePerPhoto = 10,
  allowMultiple = true,
  className = ''
}: PhotoUploadProps) => {
  const [dragActive, setDragActive] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [error, setError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File): string | null => {
    // Check file type
    if (!file.type.startsWith('image/')) {
      return 'Solo se permiten archivos de imagen';
    }

    // Check file size
    if (file.size > maxSizePerPhoto * 1024 * 1024) {
      return `El archivo debe ser menor a ${maxSizePerPhoto}MB`;
    }

    return null;
  };

  const handleFiles = (files: FileList) => {
    setError('');
    const fileArray = Array.from(files);
    
    // Check total number of files
    if (selectedFiles.length + fileArray.length > maxPhotos) {
      setError(`Máximo ${maxPhotos} fotos permitidas`);
      return;
    }

    const validFiles: File[] = [];
    const newPreviews: string[] = [];

    for (const file of fileArray) {
      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        return;
      }

      validFiles.push(file);
      newPreviews.push(URL.createObjectURL(file));
    }

    const updatedFiles = [...selectedFiles, ...validFiles];
    const updatedPreviews = [...previews, ...newPreviews];

    setSelectedFiles(updatedFiles);
    setPreviews(updatedPreviews);
    onUpload(updatedFiles);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const removeFile = (index: number) => {
    const updatedFiles = selectedFiles.filter((_, i) => i !== index);
    const updatedPreviews = previews.filter((_, i) => i !== index);
    
    // Revoke the object URL to prevent memory leaks
    URL.revokeObjectURL(previews[index]);
    
    setSelectedFiles(updatedFiles);
    setPreviews(updatedPreviews);
    onUpload(updatedFiles);
    setError('');
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Upload Area */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors duration-200 ${
          dragActive
            ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
            : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple={allowMultiple}
          accept="image/*"
          onChange={handleFileInput}
          className="hidden"
        />

        <div className="space-y-4">
          <div className="flex justify-center">
            <div className="p-3 bg-gray-100 dark:bg-gray-700 rounded-full">
              <Upload className="h-8 w-8 text-gray-400" />
            </div>
          </div>

          <div>
            <p className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Sube tus fotos
            </p>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Arrastra y suelta las fotos aquí, o haz clic para seleccionar
            </p>
            <button
              type="button"
              onClick={openFileDialog}
              className="inline-flex items-center px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors duration-200"
            >
              <Camera className="h-5 w-5 mr-2" />
              Seleccionar Fotos
            </button>
          </div>

          <div className="text-sm text-gray-500 dark:text-gray-400">
            <p>Formatos soportados: JPG, PNG, GIF</p>
            <p>Tamaño máximo: {maxSizePerPhoto}MB por foto</p>
            <p>Máximo {maxPhotos} fotos</p>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-700 dark:text-red-400 text-sm">{error}</p>
        </div>
      )}

      {/* Photo Previews */}
      {previews.length > 0 && (
        <div className="space-y-3">
          <h4 className="font-medium text-gray-900 dark:text-white">
            Fotos seleccionadas ({selectedFiles.length}/{maxPhotos})
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {previews.map((preview, index) => (
              <div key={index} className="relative group">
                <div className="aspect-square rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700">
                  <img
                    src={preview}
                    alt={`Preview ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
                <button
                  onClick={() => removeFile(index)}
                  className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors duration-200 opacity-0 group-hover:opacity-100"
                >
                  <X size={16} />
                </button>
                <div className="absolute bottom-2 left-2 right-2">
                  <div className="bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded">
                    {(selectedFiles[index].size / 1024 / 1024).toFixed(1)}MB
                  </div>
                </div>
              </div>
            ))}
            
            {/* Add More Button */}
            {selectedFiles.length < maxPhotos && (
              <button
                onClick={openFileDialog}
                className="aspect-square rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 flex items-center justify-center hover:border-green-500 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors duration-200"
              >
                <Plus className="h-8 w-8 text-gray-400" />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotoUpload;